import { PureComponent } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import * as AppActions from '../../../../actions/app'

import validate from './validation'
import * as ContentActions from '../../../../actions/content'
import { notify as notifyAction } from '../../../../actions/notifications'
import { MediaForm } from '../../../../components/Screens'
import { ContentTypes } from '../../../../constants'
import { mediaForm as mediaFormProps } from '../../../../types'
import { ObjectUtils, FileUtils } from '../../../../utils'

// Default start date
const defaultStartDateDate = new Date()
defaultStartDateDate.setHours(0)
defaultStartDateDate.setMinutes(0)
defaultStartDateDate.setSeconds(0)
const defaultStartDate = defaultStartDateDate.getTime()

// Default end date
const defaultEndDateDate = new Date()
defaultEndDateDate.setDate(new Date().getDate())
defaultEndDateDate.setHours(24)
defaultEndDateDate.setMinutes(0)
defaultEndDateDate.setSeconds(0)
const defaultEndDate = defaultEndDateDate.getTime() + 1000 * 60 * 60 * 24 * 30

class Media extends PureComponent {
    static propTypes = {
        clearUploadProgress: PropTypes.func.isRequired,
        saveUploadProgress: PropTypes.func.isRequired,
        editFormValue: PropTypes.func.isRequired,
        form: PropTypes.shape(mediaFormProps).isRequired,
        isOpen: PropTypes.bool,
        onClickCancel: PropTypes.func,
        onFinishSubmit: PropTypes.func,
        resetUpload: PropTypes.func.isRequired,
        uploadMedia: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        isOpen: false,
        onClickCancel: () => null,
        onFinishSubmit: () => null,
    }

    constructor(props) {
        super(props)

        this.state = {
            formErrors: {
                name: null,
                file: null,
                date_start: null,
                date_end: null,
            },
            progress: 0,
            isWorking: false,
        }

        this.onChangeFormFile = this.onChangeFormFile.bind(this)
        this.onChangeFormValue = this.onChangeFormValue.bind(this)
        this.onClickNullStartDate = this.onClickNullStartDate.bind(this)
        this.onClickNullEndDate = this.onClickNullEndDate.bind(this)
        this.onClickRemoveStartDate = this.onClickRemoveStartDate.bind(this)
        this.onClickRemoveEndDate = this.onClickRemoveEndDate.bind(this)
        this.onSubmit = this.onSubmit.bind(this)
    }

    componentDidMount() {
        this.loadData()
    }

    // On Change any input value
    onChangeFormValue({ target: { name, value } }) {
        const { editFormValue } = this.props
        editFormValue('media', { name, value })
    }

    onChangeFormFile({ target: { name, files } }) {
        const { editFormValue, form } = this.props
        editFormValue('media', { name, value: files })

        const { name: formFileName } = form

        if (!formFileName && files.length) {
            const { name: fileName } = files[0]
            const fileWithoutExtension = FileUtils.getFileWithoutExtension(fileName)
            editFormValue('media', { name: 'name', value: fileWithoutExtension })
        }
    }

    onClickNullStartDate() {
        this.onChangeFormValue({
            target: { name: 'date_start', value: defaultStartDate },
        })
    }

    onClickNullEndDate() {
        this.onChangeFormValue({
            target: { name: 'date_end', value: defaultEndDate },
        })
    }

    onClickRemoveStartDate() {
        this.onChangeFormValue({
            target: { name: 'date_start', value: null },
        })
    }

    onClickRemoveEndDate() {
        this.onChangeFormValue({
            target: { name: 'date_end', value: null },
        })
    }

    onProgress = (progress) => {
        this.setState({ progress })
    }

    onSubmit = async () => {
        if (!this.validate()) {
            return
        }

        this.setState({ isWorking: true })

        try {
            const { form, onFinishSubmit, uploadMedia } = this.props

            const { file } = form

            const { size } = file[0]

            const media = { ...form, size }

            const { clearUploadProgress, saveUploadProgress } = this.props

            const saved = await uploadMedia(file[0], media, {
                onProgress: ({ progress }) => {
                    saveUploadProgress(progress, file[0].name)
                    this.setState({ progress })
                },
            })

            clearUploadProgress()

            if (saved) {
                await onFinishSubmit([saved])
            }

            this.loadData()
        } catch (e) {
            console.error(e)
            // TODO: Notify failure
        } finally {
            this.setState({ isWorking: false })
        }
    }

    buildOnchangeObject() {
        return {
            file: this.onChangeFormFile,
            name: this.onChangeFormValue,
            date_start: (value) => this.onChangeFormValue({ target: { name: 'date_start', value } }),
            date_end: (value) => this.onChangeFormValue({ target: { name: 'date_end', value } }),
            metadata: ({ videoHeight, videoLength, videoWidth }) => {
                this.onChangeFormValue({
                    target: { name: 'height', value: videoHeight },
                })
                this.onChangeFormValue({
                    target: { name: 'length', value: videoLength },
                })
                this.onChangeFormValue({
                    target: { name: 'width', value: videoWidth },
                })
            },
        }
    }

    loadData() {
        const { resetUpload } = this.props
        resetUpload(ContentTypes.MEDIA)
        this.setState({ progress: 0 })
    }

    /**
     * Form Validation
     */
    validate() {
        const { form } = this.props
        const formErrors = validate(form)
        this.setState({ formErrors })
        return ObjectUtils.isObjValuesEmpty(formErrors)
    }

    render() {
        const { form, isOpen, onClickCancel, t } = this.props
        const { formErrors, progress, isWorking } = this.state
        return (
            <MediaForm
                errors={formErrors}
                isOpen={isOpen}
                isWorking={isWorking}
                onChangeForm={this.buildOnchangeObject()}
                onClickCancel={onClickCancel}
                onClickSubmit={this.onSubmit}
                onClickNullStartDate={this.onClickNullStartDate}
                onClickNullEndDate={this.onClickNullEndDate}
                onClickRemoveStartDate={this.onClickRemoveStartDate}
                onClickRemoveEndDate={this.onClickRemoveEndDate}
                progress={progress}
                values={form}
                t={t}
            />
        )
    }
}

const mapStateToProps = ({
    content: {
        form: { media: form },
    },
}) => ({
    form,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...ContentActions,
            notify: notifyAction,
            clearUploadProgress: AppActions.clearUploadProgress,
            saveUploadProgress: AppActions.saveUploadProgress,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Media))
